'use client';

import { useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Button } from '@/components/ui/forms/button';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { generateId } from '../../utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/layout/collapsible';

export default function CertificationsSection() {
  const { resume, addCertification, updateCertification, removeCertification } =
    useResumeBuilderStore();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  if (!resume) return null;

  const { certifications } = resume.content;

  const handleAdd = () => {
    const id = generateId();
    addCertification({
      id,
      name: '',
      issuer: '',
      date: '',
      credentialUrl: '',
    });
    setExpandedIds((prev) => new Set([...prev, id]));
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort by date descending
  const sortedCertifications = [...certifications].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

  return (
    <div className="space-y-4">
      {sortedCertifications.length === 0 ? (
        <p className="text-body text-muted-foreground">
          Add professional certifications, licenses, or credentials that are relevant to your
          target role.
        </p>
      ) : (
        <div className="space-y-3">
          {sortedCertifications.map((cert) => (
            <Collapsible
              key={cert.id}
              open={expandedIds.has(cert.id)}
              onOpenChange={() => toggleExpanded(cert.id)}
            >
              <div className="rounded-lg border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div className="flex-1">
                      <div className="text-label">{cert.name || 'New Certification'}</div>
                      {cert.issuer && (
                        <div className="text-body-sm text-muted-foreground">{cert.issuer}</div>
                      )}
                    </div>
                    {expandedIds.has(cert.id) ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4 border-t border-border p-4">
                    <div className="space-y-2">
                      <Label>Certification Name *</Label>
                      <Input
                        value={cert.name}
                        onChange={(e) => updateCertification(cert.id, { name: e.target.value })}
                        placeholder="AWS Solutions Architect"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Issuing Organization *</Label>
                      <Input
                        value={cert.issuer}
                        onChange={(e) => updateCertification(cert.id, { issuer: e.target.value })}
                        placeholder="Amazon Web Services"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Date Obtained</Label>
                      <Input
                        type="month"
                        value={cert.date || ''}
                        onChange={(e) => updateCertification(cert.id, { date: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Credential URL</Label>
                      <Input
                        type="url"
                        value={cert.credentialUrl || ''}
                        onChange={(e) =>
                          updateCertification(cert.id, { credentialUrl: e.target.value })
                        }
                        placeholder="https://..."
                      />
                      <p className="text-caption text-muted-foreground">
                        Optional. Link to verify your credential.
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeCertification(cert.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={handleAdd}>
        <Plus className="mr-2 h-4 w-4" />
        Add Certification
      </Button>
    </div>
  );
}
